import type { Messages } from './hu'

export const de: Messages = {
  meta: {
    title: 'Schwachstromanlagen',
    description:
      'Planung, Installation und Wartung von Brandmelde-, Zutrittskontroll-, Einbruchmelde-, CCTV- und Sprechanlagen in Budapest und landesweit. EN54, VdS, Grade 3 zertifiziert.',
    ogTitle: 'Brandmeldeanlagen und Sicherheitstechnik | Archestor Kft.',
    ogDescription:
      'Professionelle Schwachstromanlagen: Brandmelde-, Zutrittskontroll-, CCTV- und Einbruchmeldesysteme nach europäischen Normen.',
    ogLocale: 'de_DE'
  },
  nav: {
    home: 'Startseite',
    about: 'Über uns',
    services: 'Leistungen',
    references: 'Referenzen',
    contact: 'Kontakt',
    menu: 'Menü',
    languageSelection: 'Sprachauswahl',
    accessibilityMode: 'Sehbehindertenmodus',
    accessibilityOn: 'Sehbehindertenmodus ein',
    accessibilityOff: 'Sehbehindertenmodus aus'
  },
  hero: {
    titleLine1: 'Sicherheit,',
    titleLine2: 'auf die Sie',
    titleLine3: 'zählen können.',
    subtitle:
      'Planung, Installation und Wartung komplexer Schwachstromanlagen nach europäischen Normen — aus einer Hand, mit einer Verantwortung.',
    cta: 'Angebot anfordern',
    imageAlt: 'Schwachstrom-Sicherheitssysteme — Brandmelde- und Zutrittskontrollinstallation',
    stats: {
      projects: 'Erfolgreiche Projekte',
      standards: 'Europäische Normen',
      expertise: 'Systemkompetenz'
    }
  },
  about: {
    label: 'Über uns',
    titleLine1: 'Ungarische Expertise,',
    titleLine2: 'europäische',
    titleLine3: 'Standards.',
    text1:
      'Wir sind ein in Ungarn ansässiges Fachunternehmen, das sich ausschließlich auf die Planung, Installation und Wartung von Schwachstromanlagen konzentriert.',
    text2:
      'Wir bauen zuverlässige, intelligente und zukunftssichere Systeme für Bürogebäude, Industrieanlagen, Krankenhäuser, öffentliche Einrichtungen und Wohnanlagen.',
    imageAlt: 'Schwachstromanlagen in einer Industrieanlage — Brandmelde- und CCTV-Systeme'
  },
  services: {
    label: 'Leistungen',
    titleLine1: 'Umfassende',
    titleLine2: 'Systemintegration.',
    items: [
      {
        title: 'Brandmeldeanlagen',
        desc: 'Adressierbare oder konventionelle, ein- oder mehrzentrale Systeme nach EN54 und VdS.'
      },
      {
        title: 'Zutrittskontrollsysteme',
        desc: 'Karten-, biometrische und mobile Zutrittskontrolle — von Drehkreuzen bis zu Kennzeichenerkennungsschranken.'
      },
      {
        title: 'Einbruchmeldeanlagen',
        desc: 'Perimeterschutz, Grade-3-zertifizierte Systeme, bis zu 520 Zonen pro Zentrale.'
      },
      {
        title: 'CCTV-Überwachung',
        desc: 'HD- und 4K-IP-Kameras, intelligente Videoanalyse, Fernüberwachung.'
      },
      {
        title: 'Gaswarnanlagen',
        desc: 'Erkennung explosiver und giftiger Gase, Integration in Brandmeldeanlagen.'
      },
      {
        title: 'Löschanlagensteuerung',
        desc: 'Steuerung von Gas- und Schaumlöschanlagen, integriert in die Brandmeldeanlage.'
      },
      {
        title: 'Pflegerufsysteme',
        desc: 'Komplette Installation für Krankenhäuser, Pflegeheime und barrierefreie Sanitärräume.'
      },
      {
        title: 'Sprechanlagen',
        desc: 'Moderne Video-IP-Sprechanlagen, auch für Mehrfamilienhäuser.'
      },
      {
        title: 'IT-Netzwerke',
        desc: 'Strukturierte Verkabelung, aktive Netzwerkgeräte, Serverraumausbau.'
      },
      {
        title: 'Softwareentwicklung',
        desc: 'Individuelle Sicherheitssystementwicklung über unser umfangreiches Partnernetzwerk.'
      },
      {
        title: 'Digitale Protokollierung',
        desc: 'Umfassende digitale Protokollierung für Brandschutz- und Gebäudewartungsaufgaben.'
      },
      {
        title: 'Fernüberwachung',
        desc: 'Als registrierter Errichter: Signalübertragung von Brand- und Einbruchmeldeanlagen mit Einsatzdienst.'
      }
    ]
  },
  references: {
    label: 'Referenzen',
    titleLine1: 'Stolz auf',
    titleLine2: 'unsere bisherige Arbeit.',
    more: '...und viele weitere erfolgreiche Projekte.',
    items: [
      {
        tag: 'Bürogebäude • Budapest (Budapart) • 2025-2026',
        title:
          'Zutrittskontroll-, Einbruchmelde-, Pflegeruf- und Sprechanlage in einem modernen Bürokomplex',
        desc: 'Wir sind stolz darauf, als Subunternehmer an einem großen Flaggschiff-Projekt teilgenommen zu haben und Zutrittskontroll-, Einbruchmelde-, Pflegeruf- und Sprechanlagen über 21 Etagen installiert zu haben.',
        alt: 'Budapart Bürogebäude — Zutrittskontrolle, Einbruchmelde- und Sprechanlage'
      },
      {
        tag: 'Industrie • Szikszó • 2023-',
        title: 'Integrierte Schwachstromanlagen in Produktions- und Logistikgebäude',
        desc: 'Integrierte Installation und Wartung von Brandmelde-, Löschanlagensteuerungs-, Gaswarn-, Zutrittskontroll-, Einbruchmelde- und CCTV-Systemen als Subunternehmer.',
        alt: 'Industrieanlage Szikszó — integrierte Brandmelde-, Zutrittskontroll- und CCTV-Systeme'
      },
      {
        tag: 'Bürogebäude • Budapest (Angyalföld) • 2026',
        title: 'Fit-out-Umbau eines Bürogebäudes',
        desc: 'Fit-out-Umbau der Brandmeldeanlage über mehrere Etagen für unseren Partner.',
        alt: 'Bürogebäude Angyalföld — Brandmeldeanlagen-Fit-out'
      },
      {
        tag: 'Logistikpark • Polgár • 2025',
        title: 'Kennzeichenerkennungs-Zutrittskontrolle in einem Logistikpark',
        desc: 'Automatisierte Kennzeichenerkennungsschranke mit 24/7-Protokollierung und Notruf-Sprechanlage.',
        alt: 'Logistikpark Polgár — Kennzeichenerkennung und Sprechanlage'
      },
      {
        tag: 'Bürogebäude • Budapest (City Park) • 2025',
        title: 'Zutrittskontroll- und Einbruchmeldesystem in einem modernen Bürogebäude des 21. Jahrhunderts',
        desc: 'Auf Einladung unseres Partners haben wir Sicherheitssysteme für einen großflächigen Bürokomplex im Herzen von Budapest geliefert und in Betrieb genommen.',
        alt: 'City Park Bürogebäude — moderne Zutrittskontrolle und Einbruchmeldeanlage'
      },
      {
        tag: 'Wohnanlage • Budapest (Zugló) • 2025',
        title: 'Brandmeldeanlage in einer Wohnanlage',
        desc: 'Inbetriebnahme einer Brandmeldeanlage.',
        alt: 'Wohnanlage Zugló — Installation einer Brandmeldeanlage'
      },
      {
        tag: 'Bürogebäude • Budapest • 2024',
        title: 'Sanierung eines Bürogebäudes',
        desc: 'Sanierung der Brandmeldeanlage mit Integration bestehender Systemkomponenten.',
        alt: 'Bürosanierung Budapest — Modernisierung der Brandmeldeanlage'
      },
      {
        tag: 'Bürogebäude • Budapest (Kelenföld) • 2024-2025',
        title: 'Fit-out-Umbau eines Bürogebäudes',
        desc: 'Fit-out-Umbau von Brandmelde-, Zutrittskontroll- und Sprechanlagen über mehrere Etagen als Subunternehmer für unseren Partner.',
        alt: 'Bürogebäude Kelenföld — Brandmelde- und Zutrittskontroll-Fit-out'
      },
      {
        tag: 'Industrie • Debrecen • 2023-2025',
        title: 'Brandmeldeanlage in einem Produktionskomplex',
        desc: 'Als Subunternehmer haben wir die Brandmeldearbeiten für eine der größten regionalen Entwicklungen — einen Produktionskomplex — überwacht und in Betrieb genommen.',
        alt: 'Produktionskomplex Debrecen — Installation und Überwachung der Brandmeldeanlage'
      }
    ]
  },
  contact: {
    label: 'Kontakt',
    titleLine1: 'Lassen Sie uns',
    titlePrefix: '',
    titleEmphasis: 'zusammenarbeiten.',
    intro:
      'Teilen Sie uns Ihre Vorstellungen mit — unsere Experten melden sich in Kürze mit einem maßgeschneiderten Angebot.',
    emailLabel: 'E-Mail',
    companyLabel: 'Firmendaten',
    companyName: 'Archestor Kft.',
    companyAddress: 'Sitz: Salgótarján, Arany János út 17.',
    companyReg: 'Handelsregisternummer: 12-09-012726',
    companyTax: 'USt-IdNr.: HU32403237',
    formSubject: 'Neue Angebotsanfrage - Archestor Kft.',
    namePlaceholder: 'Name *',
    companyPlaceholder: 'Unternehmen',
    emailPlaceholder: 'E-Mail-Adresse *',
    messagePlaceholder: 'Projektbeschreibung…',
    submit: 'Senden',
    success: 'Vielen Dank! Wir werden uns in Kürze bei Ihnen melden.',
    captchaRequired: 'Bitte füllen Sie das Captcha vor dem Senden aus!',
    captchaFailed: 'Captcha-Überprüfung fehlgeschlagen.',
    captchaError: 'Bei der Captcha-Überprüfung ist ein Fehler aufgetreten.',
    sendError: 'Beim Senden ist ein Fehler aufgetreten.'
  },
  footer: {
    rights: 'Alle Rechte vorbehalten.',
    cookieNotice: 'Datenschutzhinweis',
    cookieSettings: 'Cookie-Einstellungen'
  },
  renameNotice: {
    title: 'Unser Firmenname hat sich geändert',
    body: 'Der neue Name der Fusion Safety Kft.: Archestor Kft.\nNur unser Name hat sich geändert. Die Unternehmensdaten bleiben unverändert, und unser Team arbeitet in der gewohnten hohen Qualität weiter.',
    close: 'Verstanden'
  },
  cookies: {
    bannerTitle: 'Cookies und Datenschutz',
    bannerDescription:
      'Diese Website verwendet Cookies für den Betrieb, den Spam-Schutz des Kontaktformulars (Cloudflare Turnstile) und — mit Ihrer Einwilligung — zum Speichern von Sprach- und Komforteinstellungen. Sie können Ihre Einstellungen jederzeit ändern.',
    acceptAll: 'Akzeptieren',
    rejectAll: 'Ablehnen',
    contactFormBlocked:
      'Das Kontaktformular ist aufgrund Ihrer Cookie-Einstellungen nicht verfügbar. Um eine E-Mail zu senden, akzeptieren Sie bitte die Cookies.'
  },
  cookieNotice: {
    title: 'Datenschutzhinweis',
    subtitle: 'Informationen zur cookiebezogenen Datenverarbeitung auf archestor.com und seinen Unterseiten',
    close: 'Schließen',
    sections: [
      {
        title: '1. Begriff und Zweck von Cookies',
        body: [
          'Cookies sind kleine Datendateien, die der Server der besuchten Website an Ihren Browser sendet und auf Ihrem Gerät speichert.',
          'Auf archestor.com werden Cookies verwendet, um die Website sicher und komfortabel zu betreiben, Ihre Cookie-Auswahl zu speichern, das Kontaktformular vor Spam zu schützen und — mit Ihrer Einwilligung — Sprach- und Komforteinstellungen zu merken.'
        ]
      },
      {
        title: '2. Cookies akzeptieren oder ablehnen',
        body: [
          'Auf dieser Website können Sie keine einzelnen Cookie-Kategorien separat auswählen: Sie akzeptieren entweder alle Cookies oder lehnen sie ab.',
          'Bei Akzeptanz verwendet die Website Speicher für den Betrieb, das Merken von Sprach- und Komforteinstellungen sowie den Spam-Schutz des Kontaktformulars (Cloudflare Turnstile).',
          'Bei Ablehnung setzen wir keine Cookies und das Kontaktformular ist nicht verfügbar.',
          'Diese Website verwendet keine Statistik-, Werbe- oder Marketing-Cookies.'
        ]
      },
      {
        title: '3. Verwaltung der Einwilligung',
        body: [
          'Beim ersten Besuch erscheint ein Cookie-Banner, in dem Sie Cookies akzeptieren oder ablehnen können. Bei Akzeptanz werden notwendige und funktionale Cookies aktiviert; bei Ablehnung werden nicht notwendige Speicher gelöscht und das Kontaktformular ist nicht verfügbar.',
          'Sie können Ihre Auswahl jederzeit über die Schaltfläche „Cookie-Einstellungen“ in der Fußzeile ändern. Eine zuvor erteilte Einwilligung kann durch Ablehnung widerrufen werden.'
        ]
      },
      {
        title: '4. Cookie-Einstellungen im Browser',
        body: [
          'Cookies können auch in den Browsereinstellungen gesteuert werden. Das Blockieren oder Löschen von Cookies kann die Nutzbarkeit einiger Funktionen beeinträchtigen.'
        ]
      }
    ],
    tableTitle: '5. Eigenschaften der einzelnen Cookies',
    tableHeaders: {
      name: 'Name',
      function: 'Funktion',
      type: 'Typ',
      duration: 'Dauer',
      purpose: 'Zweck',
      legalBasis: 'Rechtsgrundlage'
    },
    rows: [
      {
        name: 'archestor-consent',
        function: 'Speichert Cookie-Einwilligung',
        type: 'HTTP-Cookie',
        duration: '1 Jahr',
        purpose: 'Speichert die Cookie-Auswahl des Nutzers',
        legalBasis: 'Berechtigtes Interesse / Einwilligung'
      },
      {
        name: 'archestor-locale',
        function: 'Spracheinstellung',
        type: 'HTTP-Cookie',
        duration: '1 Jahr',
        purpose: 'Speichert die gewählte Sprache',
        legalBasis: 'Einwilligung'
      },
      {
        name: 'archestor-country',
        function: 'Ländercode-Speicherung',
        type: 'HTTP-Cookie',
        duration: '24 Stunden',
        purpose: 'Unterstützt länderbasierte Sprachvorschläge',
        legalBasis: 'Einwilligung'
      },
      {
        name: 'archestor-a11y',
        function: 'Barrierefreiheitsmodus',
        type: 'Lokaler Speicher (localStorage)',
        duration: 'Bis zur Löschung',
        purpose: 'Speichert die Einstellung für den Sehbehindertenmodus',
        legalBasis: 'Einwilligung'
      },
      {
        name: 'archestor-rename-seen',
        function: 'Hinweis zur Firmennamensänderung',
        type: 'Lokaler Speicher (localStorage)',
        duration: 'Bis zur Löschung',
        purpose: 'Speichert, dass der Hinweis zur Umbenennung von Fusion Safety bereits geschlossen wurde',
        legalBasis: 'Berechtigtes Interesse'
      },
      {
        name: 'Cloudflare Turnstile',
        function: 'Spam-Schutz',
        type: 'Drittanbieter (HTTP-Cookie)',
        duration: 'Gemäß Anbieterdokumentation',
        purpose: 'Schützt das Kontaktformular vor Bots',
        legalBasis: 'Einwilligung'
      },
      {
        name: 'FormSubmit',
        function: 'E-Mail-Weiterleitung',
        type: 'Drittanbieter (HTTP-Cookie)',
        duration: 'Gemäß Anbieterdokumentation',
        purpose: 'Zustellung von Kontaktformular-Nachrichten',
        legalBasis: 'Einwilligung'
      }
    ],
    thirdPartyTitle: '6. Cookies und Datenschutz bei Drittanbietern',
    thirdPartyServices: [
      {
        description:
          'Für den Spam-Schutz des Kontaktformulars nutzen wir Cloudflare Turnstile. Turnstile wird erst nach Cookie-Akzeptanz geladen und arbeitet nach der Datenschutzrichtlinie von Cloudflare.',
        privacyLabel: 'Cloudflare-Datenschutzrichtlinie:',
        privacyUrl: 'https://www.cloudflare.com/privacypolicy/'
      },
      {
        description:
          'Zur Weiterleitung der Kontaktformular-Nachrichten nutzen wir FormSubmit (formsubmit.co). Das Senden von E-Mails ist erst nach Cookie-Akzeptanz möglich; die übermittelten Daten werden über den FormSubmit-Dienst an uns übermittelt.',
        privacyLabel: 'FormSubmit-Datenschutzrichtlinie:',
        privacyUrl: 'https://formsubmit.co/privacy.pdf',
        openInNewTab: false
      },
      {
        description:
          'Eingehende Kontakt-E-Mails werden an das Microsoft-365-Postfach (Outlook) der Archestor Kft. zugestellt. Der E-Mail-Dienst wird von Microsoft nach dessen eigener Datenschutzrichtlinie bereitgestellt.',
        privacyLabel: 'Microsoft-Datenschutzrichtlinie:',
        privacyUrl: 'https://privacy.microsoft.com/'
      }
    ],
    operatorTitle: '7. Betreiber der Website',
    operatorBody: [
      'Archestor Kft.',
      'Sitz: Salgótarján, Arany János út 17., Ungarn',
      'Handelsregisternummer: 12-09-012726',
      'E-Mail: office@archestor.com'
    ],
    hostingTitle: '8. Hosting-Anbieter',
    hostingBody: [
      'Cloudflare, Inc.',
      'Sitz: 101 Townsend Street, San Francisco, CA 94107, USA',
      'Web: https://www.cloudflare.com/',
      'E-Mail: privacy@cloudflare.com',
      'Die Website wird über das Cloudflare-Edge-Netzwerk bereitgestellt. Für Besucher aus der EU werden die Inhalte aus EU-Rechenzentren auf europäischen Servern ausgeliefert; für Besucher außerhalb der EU vom jeweils nächstgelegenen Edge-Server.'
    ],
    contactTitle: '9. Kontakt und Betroffenenrechte',
    contactBody: [
      'Bei Fragen zu Cookies und Datenverarbeitung wenden Sie sich an office@archestor.com.',
      'Sie können der auf berechtigtem Interesse beruhenden Verarbeitung widersprechen und die Löschung Ihrer Daten verlangen.'
    ]
  },
  toast: {
    emailSuccess: 'E-Mail erfolgreich gesendet! Wir melden uns bald!'
  },
  schema: {
    description:
      'In Ungarn ansässiger Spezialist für Planung, Installation und Wartung von Schwachstromanlagen (Brandmelde-, Zutrittskontroll-, CCTV- und Einbruchmeldesysteme).',
    addressLocality: 'Salgótarján',
    catalogName: 'Sicherheitstechnische Dienstleistungen',
    areaServed: [
      { type: 'Country', name: 'Ungarn' },
      { type: 'City', name: 'Budapest' }
    ],
    services: [
      {
        name: 'Brandmeldeanlagen',
        description: 'Planung und Installation von EN54- und VdS-konformen Brandmeldeanlagen.'
      },
      {
        name: 'Zutrittskontrollsysteme',
        description: 'Karten-, biometrische und kennzeichenbasierte Zutrittskontrolle.'
      },
      {
        name: 'CCTV und Einbruchmeldeanlagen',
        description: 'HD/4K-Kamerasysteme und Grade-3-Einbruchmeldeanlagen.'
      }
    ]
  }
}